import React, { useState } from 'react';
import FeedCard from 'components/feed-card';
import { ReactComponent as EditMealIcon } from 'assets/icons/edit-meal-icon.svg';
import { ReactComponent as DeleteMealIcon } from 'assets/icons/delete-meal-icon.svg';
import { ReactComponent as DuplicateMealIcon } from 'assets/icons/duplicate-meal-icon.svg';
import { ReactComponent as ConvertMealIcon } from 'assets/icons/convert-meal-icon.svg';
import MealDropdownOptions, { MealDropdownOptionType } from 'components/meal-dropdown-options';
import { SearchType } from 'utils/types';
import { Link, useNavigate } from 'react-router-dom';
import { numberWithCommas } from 'utils/functions';
import { ReactComponent as RestoreIcon } from 'assets/icons/restore.svg';
import DeleteModal from 'components/base/delete-modal';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { ROUTE_KEYS, ROUTE_PREFIX } from 'utils/constants';
import useDashboardContext from 'contexts/dashboard.context';

type MealSearchProps = {
  selectedResults: SearchType[];
  search: SearchType;
  handleChange: (result: SearchType) => void;
  singleMealClick: (result: SearchType) => void;
  deleteMeal: () => void;
};
const MealSearchReults = ({
  selectedResults,
  handleChange,
  singleMealClick,
  search,
  deleteMeal
}: MealSearchProps) => {
  const navigate = useNavigate();
  const [showRestoreCategoryModal, setShowRestoreCategoryModal] = useState(false);
  const { trash } = useDashboardContext();
  const restoreMealCategory = () => {
    setShowRestoreCategoryModal(!showRestoreCategoryModal);
  };
  const editMeal = () => {
    navigate(`${ROUTE_PREFIX.FOOD}/edit-meal/${search?.id}`);
  };
  const convertMeal = () => {
    navigate(`${ROUTE_PREFIX.FOOD}/convert-meal-to-addon/${search?.id}`, {
      state: search?.name
    });
  };
  const handleRestoreClick = async () => {
    const itemIds = selectedResults.length > 0 ? selectedResults : [search.id];
    const payload = { type: 'meal', item_ids: itemIds };
    try {
      const res: any = await apiService.post('marketing/restore-bulk-items', { data: payload });
      if (res.data) {
        restoreMealCategory();
        toast(
          <ToastMessage
            type='success'
            message={`Restored ${
              selectedResults.length > 0 ? 'items' : search?.name
            } successfully `}
          />
        );
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    }
  };

  const duplicateMeal = () => {
    navigate(`${ROUTE_PREFIX.FOOD}/duplicate-meal/${search?.id}`, {
      state: search?.name
    });
  };

  const dropdownList: MealDropdownOptionType[] = [
    {
      name: 'Edit meal',
      icon: <EditMealIcon className='w-4 h-4' />,
      onClick: editMeal
    },
    {
      name: 'Duplicate meal',
      icon: <DuplicateMealIcon className='w-4 h-4' />,
      onClick: duplicateMeal
    },
    {
      name: 'Convert meal',
      icon: <ConvertMealIcon className='w-4 h-4' />,
      onClick: convertMeal
    },
    {
      name: 'Archive meal',
      icon: <DeleteMealIcon className='w-4 h-4 text-red-5' />,
      className: 'text-red-5',
      onClick: deleteMeal
    }
  ];

  return (
    <div>
      <FeedCard
        className='w-full'
        checked={selectedResults.includes(search)}
        actions={
          selectedResults.length === 0 && !trash ? (
            <MealDropdownOptions
              onClick={() => singleMealClick(search)}
              dropdownList={dropdownList}
            />
          ) : selectedResults.length === 0 && trash ? (
            <div
              onClick={() => {
                restoreMealCategory();
              }}
              className='bg-white rounded-lg p-1 '>
              <RestoreIcon className='w-4 h-4' />
            </div>
          ) : null
        }
        showCheckBox
        image={search?.images && search?.images[0]}
        onSelect={() => handleChange(search)}>
        <Link to={`${ROUTE_KEYS.MEALS}/${search.id}/addon-categories`}>
          <div className='flex justify-between items-center mt-2'>
            <div>
              <p className='text-sm'>{search?.name}</p>
              {search?.amount && (
                <p className='text-xs text-gray-5'>&#8358;{numberWithCommas(search?.amount)}</p>
              )}
            </div>

            <div
              className='h-[40px] w-[40px] rounded-[100%]'
              style={{
                backgroundImage: `url(${search?.images && search?.images[0]})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          </div>
        </Link>
      </FeedCard>
      <DeleteModal
        show={showRestoreCategoryModal}
        type='restore'
        onClose={restoreMealCategory}
        handleDelete={() => handleRestoreClick()}
        title={`Are you sure you want to restore ${
          selectedResults.length > 0 ? 'items' : search?.name
        } ?`}
        description='This action will restore this meal'
      />
    </div>
  );
};

export default MealSearchReults;
